'use client';

import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { ICommonCategoryMeta, IPost } from '../../../models/ICategory';
import HTMLRenderer from '../../components/HTMLRenderer';
import { getPostFormattedDate, normalizeIsoDate } from '../../../utils/date';
import { ILocalizedParams } from '../../../models/ICommon';
import Button, { ButtonTheme } from '../../components/Button';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';
import FallBackImage from '../../components/FallBackImage';
import { appConfig } from '../../../config';
import ThemedImage from '../../components/ThemedImage';


type IPostWithTags = IPost & { tags?: ICommonCategoryMeta[] };

export default function PostsWrapper({
  category,
  posts,
  locale,
  allowCopyPaste,
  device
}: ILocalizedParams<{ posts: IPostWithTags[]; allowCopyPaste: boolean; category: string; device?: string; }
>) {


  const isDesktop = device === 'desktop';

  function isInViewport(element: HTMLElement) {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const topThreshold = viewportHeight * 0.5;
    return rect.top <= topThreshold && rect.bottom >= 0;
  }

  const handleScroll = (e: any) => {
    const postEls: HTMLElement[] = Array.from(document.querySelectorAll(posts.map((_, i) => `#post_${i}`).join(',')));

    for (const i in postEls) {
      const el = postEls[i];
      const post = posts[i];
      if (isInViewport(el) && !window.location.href.endsWith(post.uniqueCode)) window.history.replaceState(window.history.state, '', `${window.location.origin}/${category}/${post.uniqueCode}`);
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    const scripts: {src: string; onload?: (this: GlobalEventHandlers, ev: Event) => any;}[] = [];
    const post = posts[0];

    const content = post.content || '';

    if (content.includes('instagram-media')) {
      scripts.push({
        src: 'https://www.instagram.com/embed.js',
        onload: () => (window as any).instgrm?.Embeds?.process()
      });
    }
    if (content.includes('fb-post')) {
      scripts.push({src: 'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v19.0'});
    }
    if (content.includes('twitter-tweet')) {
      scripts.push(  {
        src: 'https://platform.twitter.com/widgets.js',
        onload: () =>  (window as any).twttr?.widgets?.load()
      })
    }
    if (content.includes('tiktok-embed')) {
      scripts.push( {
        src: 'https://www.tiktok.com/embed.js',
       },)
    }

    for (const script of scripts) {
      const el = document.createElement("script");
      el.setAttribute("src", script.src);
      el.setAttribute("async", "true");
      if (script.onload) el.onload = script.onload;
      document.body.appendChild(el);
    }
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    }
  }, []);


  const PostContent = forwardRef(({ data, id }: { data: IPostWithTags, id: string; }, ref: any) => {
    const [normalizedContent, setNormalizedContent] = useState(() => {
      return (data.content || '')
    .replaceAll(`xmlns:xlink="https://www.w3.org/1999/xlink"`, `xmlns:xlink="http://www.w3.org/1999/xlink"`)
    .replaceAll(`xmlns="https://www.w3.org/2000/svg"`, `xmlns="http://www.w3.org/2000/svg"`)
    .replaceAll(`<script async src=\"https://platform.twitter.com/widgets.js\" charset=\"utf-8\"></script>`, ``)
    // .replaceAll(/src="([^"]*)"/g, 'data-src="$1"');
    })


    // useEffect(() => {
    //   setTimeout(() => setNormalizedContent(normalizedContent.replaceAll(/data-src="([^"]*)"/g, 'src="$1"')), 1000);
    // },[]);

    return (
      <div className='flex flex-col desktop:mt-[24px]' ref={ref} id={id}>
        <div className='flex flex-col mb-[50px]'>
          <h1 className='mb-[30px] !font-kufi-700'>{data.title}</h1>
          {data.category ? <Link hrefLang={locale} title={data.category.uniqueCode} href={`/${data.category.uniqueCode}`} className='text-secondary-200 dark:text-white mb-[4px] w-fit h5'>{data.category.title}</Link> : null}

          <div className='flex items-center gap-x-[10px] [&>*]:text-md [&>*]:whitespace-nowrap flex-wrap mt-[10px]'>
            <div className='flex items-center [&>*]:flex-shrink-0 flex-wrap'>
              {data.writers.map((x, i) => (
                <Link key={String(x.uniqueCode + i)} hrefLang={locale} href={`/writers/${x.uniqueCode}`} className={twMerge('w-[56px] h-[56px] rounded-full rtl:ml-[-10%] rtl:last:ml-0 ltr:mr-[-10%] ltr:last:mr-0 border-solid border-white border-[1px] z-[1] hover:z-[2]')}>
                  <FallBackImage device={device} src={x.image} alt={x.name} title={x.name} isUser height={0} width={0} className='h-full w-full rounded-[inherit]' />
                </Link>
              ))}
            </div>

            <div className='flex flex-col gap-y-0 tablet:gap-y-[8px]'>

            <div className='flex items-center [&>*]:whitespace-nowrap flex-wrap'>
              {data.writers.map((x, i) => {
                const isLast = i === data.writers.length - 1;
                return <div key={String(x.uniqueCode + i)} className='flex items-center'>
                  <Link hrefLang={locale} title={x.name} href={`/writers/${x.uniqueCode}`} className='h3 tablet:h4 tablet:font-semibold'>{x.name}</Link>
                  {isLast ? null : <span>,&nbsp;&nbsp;</span>}
                </div>
              })}
            </div>

            <div className='flex items-center gap-x-[5px]'>
              <span className='sm font-medium' suppressHydrationWarning={true}>{getPostFormattedDate(normalizeIsoDate(data.publishedOn), locale)},</span>
              <span className='sm font-medium' suppressHydrationWarning={true}>{new Date(normalizeIsoDate(data.publishedOn)).toLocaleString(locale === 'ar' ? 'ar-AE' : 'en-US', { hour: 'numeric', minute: 'numeric', hour12: true })}</span>
            </div>
              </div>
          </div>

        </div>

        <HTMLRenderer content={normalizedContent} className={twMerge('post-inner [&>img]:w-full [&>*]:[&>img]:w-full [&>a]:text-secondary-400 [&>a]:cursor-pointer [&>a]:[&>*]:text-secondary-400 [&>a]:[&>*]:cursor-pointer text-primary-300 [&>*]:text-primary-300 dark:text-white dark:[&>*]:text-white', !allowCopyPaste && 'select-none')} />
          <Link rel='noopener noreferrer' href={appConfig.googleNewsUrl} target='_blank' className='w-full relative h-fit aspect-[348/151] tablet:aspect-[949/155] my-[20px]'>
            <ThemedImage src={`/assets/images/google-banner/light${isDesktop ? '' : '-mobile'}.png`} darkSrc={`/assets/images/google-banner/dark${isDesktop ? '' : '-mobile'}.png`} fill sizes='100vw' alt='google-banner'/>
          </Link>
        <div className='mt-[70px] flex items-center flex-wrap gap-[24px]'>
          {data.tags?.length ? data.tags.map(({ title, slug, uniqueCode },i) => <Button linkProps={{prefetch: false}} locale={locale} key={String(uniqueCode  + title + i)} href={encodeURI(`/topic/${uniqueCode || slug}`)} title={title} theme={ButtonTheme.secondary} />) : null}
        </div>
      </div>
    )
  });

  return (
    <div className='flex flex-col px-[16px] tablet:pt-[16px] desktop:pt-0 w-full tablet:px-0 pt-[24px]  tablet:w-[70%] tablet:pr-[16px] desktop:pr-[74px] ltr:tablet:pr-0 ltr:desktop:pr-0 ltr:tablet:px-[20px] ltr:desktop:px-[74px]'>
      {posts.map((p, i) => {
        const isLast = posts.length - 1 === i;
        return (<div className='flex flex-col' key={p.uniqueCode + i}>
          <PostContent id={`post_${i}`} key={`${p.uniqueCode}_${i}`} data={p} />
          {isLast ? null : <div className='w-full h-[3px] bg-secondary-400 my-[30px]' />}
        </div>)
      })}
    </div>
  )

}